/**
 * @fileoverview 動画詳細ページ・通常パターン用 Main パーシャル
 * @description 1カラムレイアウト（SingleColumnLayout）＋ロック時の会員登録モーダルフォームの「見た目」を持つ。
 *              state は親(Main)が保持し、当コンポーネントは中継層。
 *              フォーム入力イベントは v-bind="$attrs" で素通しする。$attrs は「転送」なので、
 *              親が子に付けたリスナが下層へ渡るのに加え、子が emit したイベントもそのまま親へ伝わる
 *              （中間層での再 emit は不要）。モーダル開閉等の構造系イベントのみ明示的に emit する。
 * @see https://ja.vuejs.org/guide/components/attrs.html
 */
<script lang="ts">
import { defineComponent, PropType } from 'vue'
import SingleColumnLayout from './SingleColumnLayout.vue'
import InputLead from '@/components/lead/InputLead.vue'
import UserLeadInfo from '@/components/lead/UserLeadInfo.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import InputCustomFieldItems from '@/components/customFiled/InputCustomFieldItems.vue'
import InputUserRegistrationForShowContents from '@/components/user/registration/InputUserRegistrationForShowContents.vue'
import PrivacyPolicyAgreement from '@/components/solutions/PrivacyPolicyAgreement.vue'
import { useUserStore } from '@/stores/user'

interface State {
  form: Form,
  input: {
    userInfo: {
      lastName: InputText,
      firstName: InputText,
      lastNameKana: InputText,
      firstNameKana: InputText,
      companyName: InputText,
      industry: InputText,
      scale: InputText,
      listingStatus: InputText,
      profession: InputText,
      positionLevel: InputText,
      departmentAndPosition: InputText,
      workMailAddress: InputText,
      phoneNumber: InputText,
      corporateZipcode: InputText,
      corporateAddress1: InputText,
      corporateAddress2: InputText,
      corporateNumber: InputText,
      corporateEmployeeNum: InputNumber,
    },

    optionalNewsletterSettings: {
      mailStopFlag: InputBoolean,
      mailNewsStopFlag: InputBoolean,
      mailExpoStopFlag: InputBoolean,
      mailAiStopFlag: InputBoolean,
    },

    customFields: Inputs,

    isFullFilled: boolean,
  },
  video: SolutionContentItem,
  customFieldConfigItems?: CustomFieldConfig[],
}

export default defineComponent({
  name: 'VideoMainA',

  components: {
    SingleColumnLayout,
    InputLead,
    UserLeadInfo,
    ButtonSubmit,
    InputCustomFieldItems,
    InputUserRegistrationForShowContents,
    PrivacyPolicyAgreement,
  },

  // フォーム入力イベントは $attrs で親へ素通しするため、自動継承は無効化して手動 bind する
  inheritAttrs: false,

  props: {
    state: {
      type: Object as PropType<State>,
      required: true,
    },

    isShowModal: {
      type: Boolean,
      default: false,
    },

    firstErrorDom: {
      type: Object as PropType<Element>,
      default: undefined,
    },

    isEditModeUserLeadInfo: {
      type: Boolean,
      default: false,
    },

    isUpdateUserLeadInfo: {
      type: Boolean,
      default: false,
    },

    domContentLoaded: {
      type: Boolean,
      default: false,
    },

    initialized: {
      type: Boolean,
      default: false,
    },

    submitButtonLabel: {
      type: String,
      required: true,
    },

    /**
     * コンテンツ種別の呼称（「動画」/「音声」）
     */
    mediaTypeLabel: {
      type: String,
      required: true,
    },

    privacyPolicyCaption: {
      type: String,
      default: '',
    },

    corporateBrandColor: {
      type: Object as PropType<{
        primary: string,
        secondary: string,
      }>,
      default: () => ({ primary: '', secondary: '' }),
    },
  },

  // 構造系イベントのみ宣言（フォーム入力系は $attrs 経由で親へ素通し）
  emits: [
    'update:isShowModal',
    'complete:scroll',
    'click:thumbnail',
    'validated',
    'error',
    'to-edit-mode',
  ],

  setup() {
    const userStore = useUserStore()

    return {
      userStore,
    }
  },
})
</script>

<template>
  <section class="section">
    <SingleColumnLayout
      :video="state.video"
      :corporate-brand-color="corporateBrandColor"
      @click:thumbnail="$emit('click:thumbnail')" />

    <ModalDefault
      v-if="state.video.isShowBlock"
      :is-show="isShowModal"
      :title="`会員登録して${mediaTypeLabel}を視聴する`"
      size="large"
      is-black-out
      is-blur
      :enable-backdrop="false"
      state-style-class="is-border"
      :scroll-to="firstErrorDom"
      @update:is-show="$emit('update:isShowModal', $event)"
      @complete:scroll="$emit('complete:scroll')">
      <template #description>
        <section class="f-section__viewerInfo">
          <div
            v-if="domContentLoaded"
            class="f-section__viewerInfo--body">
            <!-- ログイン時 -->
            <FormDefault
              v-if="userStore.context.isSignedIn"
              :name="state.form.name"
              :action="state.form.action"
              :method="state.form.method"
              :init-validation="true"
              @validated="$emit('validated')"
              @error="$emit('error')">
              <!--
                リード情報入力エリア
                各フォーム子に v-bind="$attrs" を付け、親(Main)が <MainA> に付けたリスナを下層へ転送する。
                $attrs は「転送」なので、子が emit したイベント（update:* / select:* / reset:* / invalid-value 等）も
                中間層で再 emit せずそのまま親へ届く。
                @see https://ja.vuejs.org/guide/components/attrs.html
              -->
              <InputLead
                v-if="isEditModeUserLeadInfo"
                v-bind="$attrs"
                :namespace="state.form.name"
                :user-lead-info="state.input.userInfo">
                <template #heading>
                  {{ mediaTypeLabel }}を視聴するには<span class="span u-text__block">
                    次の項目をご入力ください
                  </span>
                </template>
              </InputLead>

              <UserLeadInfo
                v-else
                v-bind="$attrs"
                :user-lead-info="state.input.userInfo"
                :namespace="state.form.name" />

              <!-- アンケートエリア -->
              <InputCustomFieldItems
                v-if="state.input.customFields && state.customFieldConfigItems && initialized"
                v-bind="$attrs"
                :namespace="state.form.name"
                :custom-field-config-items="state.customFieldConfigItems"
                :input-custom-fields="state.input.customFields" />

              <PrivacyPolicyAgreement :company-name="state.video.solution?.company?.name" />

              <ButtonSubmit
                name="commit"
                :caption="privacyPolicyCaption"
                :is-link="false"
                is-submit
                :color="corporateBrandColor.primary">
                {{ submitButtonLabel }}
              </ButtonSubmit>

              <input
                class="input"
                type="hidden"
                name="contact[message]"
                value="">
              <input
                v-if="isUpdateUserLeadInfo"
                class="input"
                type="hidden"
                name="contact[with_update]"
                value="1">
            </FormDefault>

            <!-- 未ログイン時（会員登録） -->
            <InputUserRegistrationForShowContents
              v-else
              v-bind="$attrs"
              :namespace="state.form.name"
              :form="state.form"
              :user-lead-info="state.input.userInfo"
              :input-custom-fields="state.input.customFields"
              :custom-field-config-items="state.customFieldConfigItems"
              :input-optional-newsletter-info="state.input.optionalNewsletterSettings"
              :submit-button-caption="privacyPolicyCaption"
              :agreement-company-name="state.video.solution?.company?.name">
              <template #step1HeadingDescription>
                無料会員登録で{{ mediaTypeLabel }}を視聴いただけます。
              </template>
              <template #step1SubmitLabel>
                登録して{{ mediaTypeLabel }}を視聴する(無料)
              </template>
              <template #step2Heading>
                {{ mediaTypeLabel }}を視聴するには次の項目をご入力ください
              </template>
              <template #step3Heading>
                {{ mediaTypeLabel }}を視聴するには次の項目をご入力ください
              </template>

              <input
                class="input"
                type="hidden"
                name="contact[message]"
                value="">

              <input
                v-if="!userStore.context.isSignedIn"
                class="input"
                type="hidden"
                name="contact[with_sign_up]"
                value="1">
            </InputUserRegistrationForShowContents>

            <p
              v-if="userStore.context.isSignedIn && !isEditModeUserLeadInfo"
              class="p u-text__center u-text__small">
              <button
                class="button"
                @click="$emit('to-edit-mode')">
                プロフィールを編集する
              </button>
            </p>
          <!-- /.f-section__viewerInfo body --></div>
        <!-- /.f-section__viewerInfo --></section>
      </template>
    </ModalDefault>
  </section>
</template>

<style lang="sass" scoped>
// foundation
.f-section__viewerInfo
  $root: &

  & &--body
    padding: 10px 16px 0
    background-color: rgba($bg-white, 1)

  +breakpoint
    #{$root}--body
      padding:
        left: 4px
        right: 4px
</style>
